import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientProjectStore } from '@/stores/service-provider/client-project-store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import templateLibrary from '@/data/template-library.json';

const PROJECT_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'mixed', label: 'Mixed Use' },
];

const STEPS = ['Project Details', 'Customer Info', 'Template', 'Review'];

const ClientProjectSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { createClientProject, isLoading, error, clearError } = useClientProjectStore();

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    projectType: 'residential',
    address: { street: '', city: '', state: '', postalCode: '', country: '' },
    customerEmail: '',
    customerName: '',
    sourceTemplateId: '',
  });

  const templates = useMemo(() => {
    const all: any[] = [];
    const lib = templateLibrary as any;
    if (lib.templates) {
      for (const category of Object.values(lib.templates) as any[][]) {
        if (Array.isArray(category)) all.push(...category);
      }
    }
    return all;
  }, []);

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.name.trim().length > 0;
      case 1: return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail);
      case 2: return true; // Template is optional
      case 3: return true;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    try {
      clearError();
      const result = await createClientProject({
        name: formData.name,
        customerEmail: formData.customerEmail,
        projectType: formData.projectType,
        customerName: formData.customerName || undefined,
        address: formData.address,
        sourceTemplateId: formData.sourceTemplateId || undefined,
      });
      navigate(`/editor/${result.project._id}`);
    } catch {
      // Error is set in store
    }
  };

  return (
    <div className="mx-auto max-w-3xl py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">New Client Project Setup</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <button
              onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                i === step
                  ? 'bg-primary text-primary-foreground'
                  : i < step
                    ? 'bg-primary/20 text-primary cursor-pointer hover:bg-primary/30'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs border border-current">
                {i < step ? '\u2713' : i + 1}
              </span>
              {label}
            </button>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Step 1: Project Details */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
            <CardDescription>Basic information about the client project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g., Smith Residence"
              />
            </div>
            <div>
              <Label htmlFor="projectType">Project Type *</Label>
              <select
                id="projectType"
                value={formData.projectType}
                onChange={(e) => updateField('projectType', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {PROJECT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="street">Street</Label>
                <Input id="street" value={formData.address.street} onChange={(e) => updateAddress('street', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" value={formData.address.city} onChange={(e) => updateAddress('city', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" value={formData.address.state} onChange={(e) => updateAddress('state', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input id="postalCode" value={formData.address.postalCode} onChange={(e) => updateAddress('postalCode', e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Customer Info */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Customer Information</CardTitle>
            <CardDescription>Who will own this project after handoff?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="customerEmail">Customer Email *</Label>
              <Input
                id="customerEmail"
                type="email"
                value={formData.customerEmail}
                onChange={(e) => updateField('customerEmail', e.target.value)}
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <Label htmlFor="customerName">Customer Name</Label>
              <Input
                id="customerName"
                value={formData.customerName}
                onChange={(e) => updateField('customerName', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Template Selection */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Choose a Template</CardTitle>
            <CardDescription>Optional -- select a starting template for the project dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              <button
                onClick={() => updateField('sourceTemplateId', '')}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  !formData.sourceTemplateId ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="font-medium text-sm">Blank Project</div>
                <div className="text-xs text-muted-foreground mt-1">Start from scratch</div>
              </button>
              {templates.map((tmpl: any) => (
                <button
                  key={tmpl.id}
                  onClick={() => updateField('sourceTemplateId', tmpl.id)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    formData.sourceTemplateId === tmpl.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-medium text-sm">{tmpl.name}</div>
                  {tmpl.description && (
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{tmpl.description}</div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tmpl.tags?.slice(0, 3).map((tag: string) => (
                      <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Create</CardTitle>
            <CardDescription>Confirm the details before creating</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>
                <span className="ml-2 font-medium">{formData.name}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Type:</span>
                <span className="ml-2 font-medium capitalize">{formData.projectType}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Email:</span>
                <span className="ml-2 font-medium">{formData.customerEmail}</span>
              </div>
              {formData.customerName && (
                <div>
                  <span className="text-muted-foreground">Customer:</span>
                  <span className="ml-2 font-medium">{formData.customerName}</span>
                </div>
              )}
              {formData.address.city && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Address:</span>
                  <span className="ml-2 font-medium">
                    {[formData.address.street, formData.address.city, formData.address.state, formData.address.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}
              <div className="col-span-2">
                <span className="text-muted-foreground">Template:</span>
                <span className="ml-2 font-medium">
                  {formData.sourceTemplateId
                    ? templates.find((t: any) => t.id === formData.sourceTemplateId)?.name || formData.sourceTemplateId
                    : 'Blank Project'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => step > 0 ? setStep(step - 1) : navigate('/service-provider/dashboard')}
        >
          {step > 0 ? 'Back' : 'Cancel'}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
            Next
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={isLoading || !canProceed()}>
            {isLoading ? 'Creating...' : 'Create Client Project'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ClientProjectSetupPage;
